import { Checkbox } from "@/components/ui/checkbox";
import { DataTableStatic } from "@/components/datatable/data-table-static";
import { Label } from "@/components/ui/label";
import { FormDatePickerV2, FormSelect } from "@/components/forms";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "@/components/ui/accordion";
import { SimpleTable } from "../simple-table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { ColumnDef } from "@tanstack/react-table";
import dayjs from "dayjs";
import {
  AddJobOrderWorkflow,
  JobOrderWorkflow,
  JobOrderWorkflowMaster,
  JobOrderWorkflowWorkchestra,
  UpdateJobOrderWorkflowDateBatch,
  UpdateJobOrderWorkflowDate
} from "@/hooks/services/pps/handlers/job-order-workflow";

import {
  TechnicianConflicts,
  TechnicianList
} from "@/hooks/services/pps/handlers/technicians-api";
import type {
  TechnicianAvailabilityIssueDto,
  TechnicianConflictDto
} from "@/hooks/services/pps/types/technicians.type";
import {
  MachineConflicts,
  MachineList
} from "@/hooks/services/pps/handlers/machines-api";
import type {
  MachineAvailabilityIssueDto,
  MachineConflictDto
} from "@/hooks/services/pps/types/machines.type";
import { GetJobWorkload } from "@/hooks/services/pps/handlers/workload-api";
import {
  useGetData,
  useGetConditionalData,
  usePostData,
  usePutData,
  useInvalidateQueries
} from "@/hooks/services/use-query-hooks";
import { cn } from "@/lib/utils";
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  Info,
  Pencil,
  Plus,
  RotateCw,
  Save,
  Search
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toastManager } from "@/lib/toast-manager";

import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { useJobOrderEddNotifications } from "@/hooks/use-edd-notification";
import { useWorkSpaceStore } from "@/stores/store";
import {
  parseProcessName,
  ProcessSelectionRow,
  SelectedProcessOverview
} from "../workflow-process-selection";

const schema = z
  .object({
    startDate: z.string().optional().nullable(),
    eddDate: z.string().optional().nullable(),
    technicianId: z.string().optional().nullable(),
    machineId: z.string().optional().nullable()
  })
  .superRefine((values, context) => {
    const hasStartDate = Boolean(values.startDate);
    const hasEddDate = Boolean(values.eddDate);

    if (hasStartDate && hasEddDate) {
      const startDate = dayjs(values.startDate);
      const actualEndDate = dayjs(values.eddDate);

      if (
        startDate.isValid() &&
        actualEndDate.isValid() &&
        actualEndDate.isBefore(startDate)
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["eddDate"],
          message: "Actual End Date must be greater than or equal to Start Date"
        });
      }
    }
  });

type WorkflowFormValues = {
  startDate: string;
  eddDate: string;
  technicianId: string;
  machineId: string;
};

type WorkflowFormValuesInput = {
  startDate?: string | null;
  eddDate?: string | null;
  technicianId?: string | null;
  machineId?: string | null;
};

const normalizeFormValue = (value?: string | number | null): string => {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value);
};

const toWorkflowFormValues = (
  value?: WorkflowFormValuesInput
): WorkflowFormValues => ({
  startDate: normalizeFormValue(value?.startDate),
  eddDate: normalizeFormValue(value?.eddDate),
  technicianId: normalizeFormValue(value?.technicianId),
  machineId: normalizeFormValue(value?.machineId)
});

const buildFormValuesFromProcess = (process: any): WorkflowFormValues =>
  toWorkflowFormValues({
    startDate: process?.startDate,
    eddDate: process?.eddDate,
    technicianId: process?.technicianId,
    // Prefer the explicitly saved machineId; fall back to the part-process
    // designated machine so the payload always carries the correct value.
    machineId: process?.machineId ?? process?.designatedMachineId
  });

const areWorkflowFormValuesEqual = (
  left: WorkflowFormValues,
  right: WorkflowFormValues
) =>
  left.startDate === right.startDate &&
  left.eddDate === right.eddDate &&
  left.technicianId === right.technicianId &&
  left.machineId === right.machineId;

const normalizeDateTimePayload = (value?: string): string | null => {
  if (!value) {
    return null;
  }

  const parsed = dayjs(value);
  if (!parsed.isValid()) {
    return null;
  }

  return parsed.format("YYYY-MM-DDTHH:mm");
};

const normalizeForeignKeyValue = (value?: string): number | null => {
  if (!value) {
    return null;
  }

  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const getMutationErrorMessage = (error: any, fallback: string): string => {
  if (Array.isArray(error?.message) && error.message.length > 0) {
    return error.message.join(" ");
  }

  if (typeof error?.message === "string" && error.message.trim()) {
    return error.message;
  }

  return fallback;
};

const getWorkflowValidationErrorMessage = (
  values: WorkflowFormValues
): string | null => {
  const result = schema.safeParse(values);

  if (result.success) {
    return null;
  }

  const issue = result.error.issues[0];
  return issue?.message || "Invalid workflow values.";
};

const renderConflictTooltipContent = ({
  resourceLabel,
  availabilityIssues,
  conflicts
}: {
  resourceLabel: "Machine" | "Technician";
  availabilityIssues:
    | TechnicianAvailabilityIssueDto[]
    | MachineAvailabilityIssueDto[];
  conflicts: TechnicianConflictDto[] | MachineConflictDto[];
}) => {
  // Avoid repeating overlap information when the conflict list is already shown.
  const compactIssues =
    conflicts.length > 0
      ? availabilityIssues.filter((issue) => issue.code !== "WORKLOAD_OVERLAP")
      : availabilityIssues;
  const visibleConflicts = conflicts.slice(0, 2);
  const hiddenConflictCount = conflicts.length - visibleConflicts.length;

  return (
    <>
      {compactIssues.length > 0 && (
        <>
          <p className="aero:font-semibold aero:mb-1">
            {resourceLabel} status:
          </p>
          {compactIssues.map((issue, index) => (
            <p key={`${issue.code}-${index}`} className="aero:text-xs">
              {issue.message}
            </p>
          ))}
        </>
      )}
      {visibleConflicts.length > 0 && (
        <>
          <p
            className={cn(
              "aero:font-semibold aero:mb-1",
              compactIssues.length > 0 && "aero:mt-2"
            )}
          >
            {resourceLabel} overlap:
          </p>
          {visibleConflicts.map((conflict) => (
            <p key={conflict.workflowId} className="aero:text-xs">
              {conflict.jobOrderNo} — {conflict.processName}
            </p>
          ))}
          {hiddenConflictCount > 0 && (
            <p className="aero:text-xs">+{hiddenConflictCount} more</p>
          )}
        </>
      )}
    </>
  );
};

export function ProcessContent({
  jobOrder,
  jobEdd,
  isDrawerOpen,
  initialWorkflowMode = "view",
  canManage = true // WOR-1793: Add canManage prop
}: {
  jobOrder: { id: number; jobOrderNo: string; partId?: number | null };
  jobEdd?: string | Date | null;
  isDrawerOpen?: boolean;
  initialWorkflowMode?: "view" | "edit";
  canManage?: boolean;
}) {
  const form = useForm({
    defaultValues: {
      startDate: "",
      eddDate: "",
      technicianId: "",
      machineId: ""
    },
    mode: "onChange",
    resolver: zodResolver(schema)
  });

  const { jobOrderUpdates, clearUpdates } = useJobOrderEddNotifications(
    jobOrder.id
  );

  const invalidate = useInvalidateQueries();

  const startDate = form.watch("startDate");
  const currentActualEndDate = form.watch("eddDate");
  const selectedTechnicianId = form.watch("technicianId");
  const selectedMachineId = form.watch("machineId");
  const isStartDateDirty = Boolean(form.formState.dirtyFields.startDate);
  const addJobOrderWorkflow = usePostData(AddJobOrderWorkflow());
  const updateWorkflowDate = usePutData(UpdateJobOrderWorkflowDate());
  const updateWorkflowDateBatch = usePutData(UpdateJobOrderWorkflowDateBatch());
  const jobOrderWorkflowDetail = useGetData(JobOrderWorkflow(jobOrder.id));
  const jobOrderWorkflowWorkchestra = useGetConditionalData(
    JobOrderWorkflowWorkchestra(jobOrder.jobOrderNo)
  );
  const { workspace } = useWorkSpaceStore();

  const [selectedProcess, setSelectedProcess] = useState<any>({});

  const technicianList = useGetData(
    TechnicianList({
      page: 1,
      pageSize: 100,
      search: "",
      workspaceId: workspace?.id,
      machineId: selectedMachineId ? Number(selectedMachineId) : undefined
    }),
    {
      enabled: Boolean(workspace?.id && isDrawerOpen)
    }
  );

  const machineListQuery = useGetData(
    MachineList({
      page: 1,
      pageSize: 100,
      search: "",
      workspaceId: workspace?.id,
      shopId: selectedProcess?.shopId ?? undefined
    }),
    {
      enabled: Boolean(workspace?.id && isDrawerOpen && selectedProcess?.shopId)
    }
  );
  const machineOptions = ((machineListQuery?.data as any)?.data || []).map(
    (m: any) => ({ value: String(m.id), label: m.name })
  );

  const conflictQuery = useGetData(
    TechnicianConflicts({
      technicianId: Number(selectedTechnicianId),
      startDate: startDate ?? "",
      // Use || so an empty string ("") also falls through to the next fallback.
      // An absent end date means: check from startDate onward (backend defaults to startDate).
      endDate: currentActualEndDate || startDate || "",
      excludeWorkflowId: selectedProcess?.id
    }),
    {
      enabled: Boolean(
        selectedTechnicianId &&
        Number(selectedTechnicianId) > 0 &&
        startDate &&
        selectedProcess?.id
      )
    }
  );
  const technicianConflicts: TechnicianConflictDto[] =
    conflictQuery?.data?.data ?? [];
  const technicianAvailabilityIssues: TechnicianAvailabilityIssueDto[] =
    conflictQuery?.data?.availabilityIssues ?? [];
  const hasConflict =
    technicianConflicts.length > 0 || technicianAvailabilityIssues.length > 0;

  const machineConflictQuery = useGetData(
    MachineConflicts({
      machineId: Number(selectedMachineId),
      startDate: startDate ?? "",
      endDate: currentActualEndDate || startDate || "",
      excludeWorkflowId: selectedProcess?.id
    }),
    {
      enabled: Boolean(
        selectedMachineId &&
        Number(selectedMachineId) > 0 &&
        startDate &&
        selectedProcess?.id
      )
    }
  );
  const machineConflicts: MachineConflictDto[] =
    machineConflictQuery?.data?.data ?? [];
  const machineAvailabilityIssues: MachineAvailabilityIssueDto[] =
    machineConflictQuery?.data?.availabilityIssues ?? [];
  const hasMachineConflict =
    machineConflicts.length > 0 || machineAvailabilityIssues.length > 0;

  const [processDrafts, setProcessDrafts] = useState<
    Record<number, WorkflowFormValues>
  >({});
  const [selectedProcesses, setSelectedProcesses] = useState<
    ProcessSelectionRow[]
  >([]);
  const [expandedProcessId, setExpandedProcessId] = useState<string>("");

  const [processSelectionTab, setProcessSelectionTab] = useState<
    "manual" | "workchestra"
  >("manual");
  const [processSearchTerm, setProcessSearchTerm] = useState("");
  const [showProcesses, setShowProcesses] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [useWorkchestraAsMainFlow, setUseWorkchestraAsMainFlow] =
    useState(false);
  const hasFetchedWorkchestraTemplate = useRef(false);
  const hasInitializedProcessSelection = useRef(false);
  const lastAutoFilledStartDateRef = useRef<string | null>(null);

  // Refetch workflow detail whenever drawer opens
  useEffect(() => {
    if (isDrawerOpen && jobOrder.id) {
      setProcessDrafts({});
      jobOrderWorkflowDetail.refetch();
      setIsEditMode(initialWorkflowMode === "edit");
      setShowProcesses(false);
    }
  }, [isDrawerOpen, jobOrder.id, initialWorkflowMode]);

  const currentFormValues = useMemo(
    () =>
      toWorkflowFormValues({
        startDate,
        eddDate: currentActualEndDate,
        technicianId: selectedTechnicianId,
        machineId: selectedMachineId
      }),
    [currentActualEndDate, selectedMachineId, selectedTechnicianId, startDate]
  );

  const selectedProcessFormValues = useMemo(
    () => buildFormValuesFromProcess(selectedProcess),
    [selectedProcess]
  );

  const hasUnsavedChanges = useMemo(() => {
    if (!selectedProcess?.id) {
      return false;
    }

    return !areWorkflowFormValuesEqual(
      currentFormValues,
      selectedProcessFormValues
    );
  }, [currentFormValues, selectedProcess?.id, selectedProcessFormValues]);

  const hasAnyUnsavedChanges = useMemo(
    () => hasUnsavedChanges || Object.keys(processDrafts).length > 0,
    [hasUnsavedChanges, processDrafts]
  );

  const isCurrentDraftValid = useMemo(() => {
    if (!hasUnsavedChanges) {
      return true;
    }

    return schema.safeParse(currentFormValues).success;
  }, [currentFormValues, hasUnsavedChanges]);

  const hasInvalidDrafts = useMemo(() => {
    const draftsToValidate: Record<number, WorkflowFormValues> = {
      ...processDrafts
    };

    if (selectedProcess?.id && hasUnsavedChanges) {
      draftsToValidate[Number(selectedProcess.id)] = currentFormValues;
    }

    return Object.values(draftsToValidate).some(
      (values) => !schema.safeParse(values).success
    );
  }, [
    currentFormValues,
    hasUnsavedChanges,
    processDrafts,
    selectedProcess?.id
  ]);

  const upsertProcessDraft = useCallback(
    (process: any, values: WorkflowFormValues) => {
      if (!process?.id) {
        return;
      }

      const processId = Number(process.id);
      if (!Number.isFinite(processId)) {
        return;
      }

      const normalizedValues = toWorkflowFormValues(values);
      const originalValues = buildFormValuesFromProcess(process);

      setProcessDrafts((prev) => {
        const next = { ...prev };

        if (areWorkflowFormValuesEqual(normalizedValues, originalValues)) {
          delete next[processId];
          return next;
        }

        next[processId] = normalizedValues;
        return next;
      });
    },
    []
  );

  const clearProcessDraft = useCallback((processId?: number) => {
    if (!processId) {
      return;
    }

    setProcessDrafts((prev) => {
      if (!prev[processId]) {
        return prev;
      }

      const next = { ...prev };
      delete next[processId];
      return next;
    });
  }, []);

  useEffect(() => {
    if (
      jobOrderUpdates &&
      Object.keys(jobOrderUpdates).length &&
      jobOrderUpdates?.data?.filter((i: any) => i.isOverdue).length
    ) {
      jobOrderWorkflowDetail.refetch().then(({ data }: any) => {
        if (Object.keys(selectedProcess).length) {
          setSelectedProcess(
            data?.data?.find((i: any) => i.id === selectedProcess.id)
          );
        }
        clearUpdates();
      });
    }
  }, [jobOrderUpdates]);

  const calculateSuggestedEDD = (startDate: string, standardHours: number) => {
    if (!startDate || !standardHours) return null;

    const start = dayjs(startDate);
    const result = start.add(standardHours, "hour");

    return result.format("YYYY-MM-DDTHH:mm");
  };

  const workflowDetails = jobOrderWorkflowDetail?.data?.data || [];
  const workflowStatusById = useMemo(() => {
    const statusMap = new Map<
      number,
      {
        label: string;
        textClassName: string;
      }
    >();

    const firstIncompleteIndex = workflowDetails.findIndex(
      (process: any) => !process?.eddDate
    );

    workflowDetails.forEach((process: any, index: number) => {
      if (process?.eddDate) {
        statusMap.set(process.id, {
          label: "Completed",
          textClassName: "aero:!text-emerald-600"
        });
        return;
      }

      if (index === firstIncompleteIndex && process?.startDate) {
        statusMap.set(process.id, {
          label: "On Progress",
          textClassName: "aero:!text-[#245BFF]"
        });
        return;
      }

      statusMap.set(process.id, {
        label: "Not Started",
        textClassName: "aero:!text-neutral-500"
      });
    });

    return statusMap;
  }, [workflowDetails]);

  const workflowsFromWorkchestra =
    jobOrderWorkflowWorkchestra?.data?.data?.workflows || [];
  const hasProcesses = workflowDetails.length > 0;
  const technicianOptions = (technicianList?.data?.data || []).map(
    (technician: any) => ({
      value: String(technician.id),
      label: technician.name
    })
  );

  const formatDisplayDateTime = (value?: string | Date | null) => {
    if (!value) {
      return "dd/mm/yyyy";
    }

    return dayjs(value).format("DD/MM/YYYY - HH:mm:ss");
  };

  const formatDateOrPlaceholder = (value?: string | null) => {
    if (!value) {
      return "dd/mm/yyyy";
    }

    return dayjs(value).format("DD/MM/YYYY - HH:mm:ss");
  };

  const formatStandardHour = (value?: number) => {
    if (!value) {
      return "0h";
    }

    return Number.isInteger(value) ? `${value}h` : `${value.toFixed(2)}h`;
  };

  const isActualEndDateOverdueByJobEdd = (
    actualEndDateValue?: string | null,
    overallJobEdd?: dayjs.Dayjs | null
  ) => {
    if (!actualEndDateValue || !overallJobEdd) {
      return false;
    }

    const actualEndDate = dayjs(actualEndDateValue);
    if (!actualEndDate.isValid() || !overallJobEdd.isValid()) {
      return false;
    }

    return actualEndDate.isAfter(overallJobEdd);
  };

  const jobEddDate = useMemo(() => {
    if (!jobEdd) {
      return null;
    }

    const parsed = dayjs(jobEdd);
    return parsed.isValid() ? parsed : null;
  }, [jobEdd]);

  const jobEddDisplay = jobEddDate
    ? jobEddDate.format("DD/MM/YYYY - HH:mm:ss")
    : "Not set";

  const jobEddStatus = useMemo(() => {
    if (!jobEddDate) {
      return null;
    }

    const now = dayjs();
    const hoursUntilDue = jobEddDate.diff(now, "hour");

    if (hoursUntilDue < 0) {
      return {
        label: "Overdue",
        className: "aero:border-red-200 aero:bg-red-50 aero:text-red-700"
      };
    }

    if (hoursUntilDue <= 48) {
      return {
        label: "Due Soon",
        className: "aero:border-amber-200 aero:bg-amber-50 aero:text-amber-700"
      };
    }

    return {
      label: "On Track",
      className:
        "aero:border-emerald-200 aero:bg-emerald-50 aero:text-emerald-700"
    };
  }, [jobEddDate]);

  const mapMasterTemplateRows = (
    workflows: any[] = []
  ): ProcessSelectionRow[] => {
    return workflows
      .map((workflow: any) => {
        const partProcessId = Number(workflow.partProcessId ?? workflow.id);
        const rwwsMeta = parseProcessName(workflow.rwwsIndex || workflow.name);
        const workflowMeta = parseProcessName(workflow.name);

        return {
          id: partProcessId,
          partProcessId,
          processId: partProcessId,
          processNo: rwwsMeta.processNo,
          processName: rwwsMeta.processName,
          shopName: workflow.shopName || "-",
          processStandardHour: workflow.processStandardHours,
          labourStandardHour:
            workflow.labourStandardHours ?? workflow.processStandardHours,
          rwwsIndex:
            workflow.rwwsIndex ||
            `${rwwsMeta.processNo} - ${rwwsMeta.processName}`,
          workchestraProcessName:
            workflowMeta.processName || workflow.name || "-"
        } satisfies ProcessSelectionRow;
      })
      .filter(
        (process: ProcessSelectionRow) =>
          process.partProcessId > 0 && Number.isFinite(process.partProcessId)
      );
  };

  const mapWorkchestraTemplateRows = (
    workflows: any[] = []
  ): ProcessSelectionRow[] => {
    return workflows
      .map((workflow: any) => {
        const partProcessId = Number(workflow.partProcessId ?? workflow.id);
        const processMeta = parseProcessName(workflow.name);

        return {
          id: partProcessId,
          partProcessId,
          processId: partProcessId,
          processNo: processMeta.processNo,
          processName: processMeta.processName,
          shopName: "-",
          processStandardHour: workflow.processStandardHours,
          labourStandardHour: workflow.labourStandardHours,
          rwwsIndex: workflow.rwwsIndex || "-",
          workchestraProcessName: workflow.name || "-"
        } satisfies ProcessSelectionRow;
      })
      .filter(
        (process: ProcessSelectionRow) =>
          process.partProcessId > 0 && Number.isFinite(process.partProcessId)
      );
  };

  const workchestraRows: ProcessSelectionRow[] = useMemo(
    () => mapWorkchestraTemplateRows(workflowsFromWorkchestra),
    [workflowsFromWorkchestra]
  );

  const filteredWorkchestraRows = workchestraRows.filter((process) => {
    const keyword = processSearchTerm.trim().toLowerCase();

    if (!keyword) {
      return true;
    }

    return `${process.rwwsIndex || ""} ${process.workchestraProcessName || ""}`
      .toLowerCase()
      .includes(keyword);
  });

  const workchestraColumns = [
    {
      accessorKey: "rwwsIndex",
      header: () => (
        <span className="aero:text-sm aero:font-bold aero:text-neutral-900">
          Rwws Index
        </span>
      ),
      cell: ({ row }: any) => {
        const process = row.original as ProcessSelectionRow;

        return (
          <span className="aero:text-sm aero:text-neutral-900">
            {process.rwwsIndex || "-"}
          </span>
        );
      },
      size: 280
    },
    {
      accessorKey: "workchestraProcessName",
      header: () => (
        <span className="aero:text-sm aero:font-bold aero:text-neutral-900">
          Process Name
        </span>
      ),
      cell: ({ row }: any) => {
        const process = row.original as ProcessSelectionRow;

        return (
          <span className="aero:text-sm aero:text-neutral-900">
            {process.workchestraProcessName || "-"}
          </span>
        );
      },
      size: 260
    },
    {
      accessorKey: "processStandardHour",
      header: () => (
        <span className="aero:text-sm aero:font-bold aero:text-neutral-900">
          Process Standard Hours
        </span>
      ),
      cell: ({ row }: any) => {
        const process = row.original as ProcessSelectionRow;
        return (
          <span className="aero:text-sm aero:text-neutral-900">
            {typeof process.processStandardHour === "number"
              ? formatStandardHour(process.processStandardHour)
              : "-"}
          </span>
        );
      },
      size: 180
    },
    {
      accessorKey: "labourStandardHour",
      header: () => (
        <span className="aero:text-sm aero:font-bold aero:text-neutral-900">
          Standard Hours
        </span>
      ),
      cell: ({ row }: any) => {
        const process = row.original as ProcessSelectionRow;
        return (
          <span className="aero:text-sm aero:text-neutral-900">
            {typeof process.labourStandardHour === "number"
              ? formatStandardHour(process.labourStandardHour)
              : "-"}
          </span>
        );
      },
      size: 150
    }
  ];

  const manualFlowPreview = useGetData(
    JobOrderWorkflowMaster(jobOrder.jobOrderNo),
    {
      enabled: Boolean(showProcesses && jobOrder.jobOrderNo)
    }
  );

  const getManualFlowPartProcesses = (params: any) => {
    const stringParams = JSON.stringify(params);

    return {
      name: `GetManualFlowPartProcesses_${jobOrder.jobOrderNo}_${stringParams}`,
      handle: async () => {
        const { page, size, ...restParams } = params;
        const response = await JobOrderWorkflowMaster(
          jobOrder.jobOrderNo,
          restParams?.search || null
        ).handle();

        const mappedData = mapMasterTemplateRows(
          response?.data?.workflows || []
        );

        const currentPage = Number(page) || 1;
        const pageSize = Number(size) || 10;
        const startIndex = (currentPage - 1) * pageSize;
        const pagedData = mappedData.slice(startIndex, startIndex + pageSize);

        return {
          data: pagedData,
          totalItems: mappedData.length
        };
      }
    };
  };

  const processColumns: ColumnDef<ProcessSelectionRow>[] = [
    {
      id: "select",
      header: () => <div className="aero:w-12" />,
      cell: ({ row }) => {
        const isChecked = selectedProcesses.some(
          (process) => process.partProcessId === row.original.partProcessId
        );

        return (
          <div className="aero:flex aero:items-center aero:justify-center">
            <Checkbox
              checked={isChecked}
              onCheckedChange={(checked) => {
                if (checked) {
                  setSelectedProcesses((prev) => {
                    const exists = prev.find(
                      (process) =>
                        process.partProcessId === row.original.partProcessId
                    );

                    if (exists) {
                      return prev;
                    }

                    return [...prev, row.original];
                  });
                  return;
                }

                setSelectedProcesses((prev) =>
                  prev.filter(
                    (process) =>
                      process.partProcessId !== row.original.partProcessId
                  )
                );
              }}
            />
          </div>
        );
      },
      size: 48
    },
    {
      accessorKey: "processName",
      header: () => (
        <span className="aero:text-sm aero:font-bold aero:text-neutral-900">
          Process Name
        </span>
      ),
      cell: ({ row }) => {
        const selectedIndex = selectedProcesses.findIndex(
          (process) => process.partProcessId === row.original.partProcessId
        );

        return (
          <div className="aero:flex aero:items-center aero:gap-2">
            <div className="aero:text-sm aero:text-neutral-900">
              <span className="aero:font-medium">{row.original.processNo}</span>
              <span> - {row.original.processName}</span>
            </div>
            {selectedIndex >= 0 && (
              <span className="aero:inline-flex aero:items-center aero:rounded-full aero:border aero:border-neutral-400 aero:px-2 aero:py-0.5 aero:text-[11px] aero:font-semibold aero:leading-none aero:text-neutral-700">
                Step {selectedIndex + 1}
              </span>
            )}
          </div>
        );
      },
      size: 280
    },
    {
      accessorKey: "shopName",
      header: () => (
        <span className="aero:text-sm aero:font-bold aero:text-neutral-900">
          Shop Name
        </span>
      ),
      cell: ({ row }) => (
        <span className="aero:text-sm aero:text-neutral-900">
          {row.original.shopName}
        </span>
      ),
      size: 140
    },
    {
      accessorKey: "processStandardHour",
      header: () => (
        <span className="aero:text-sm aero:font-bold aero:text-neutral-900">
          Standard Hours
        </span>
      ),
      cell: ({ row }) => (
        <span className="aero:text-sm aero:text-neutral-900">
          {typeof row.original.processStandardHour === "number"
            ? formatStandardHour(row.original.processStandardHour)
            : "-"}
        </span>
      ),
      size: 120
    }
  ];

  const getSuggestedPlanEndDate = (
    startDateValue?: string | null,
    standardHours?: number
  ) => {
    if (!startDateValue || !standardHours) {
      return null;
    }

    return calculateSuggestedEDD(startDateValue, standardHours);
  };

  useEffect(() => {
    if (!isEditMode || !selectedProcess?.id) {
      return;
    }

    if (!isStartDateDirty) {
      return;
    }

    if (!startDate || currentActualEndDate) {
      return;
    }

    // Prevent re-applying auto-fill after user clears Actual End Date
    // for the same Start Date value.
    if (lastAutoFilledStartDateRef.current === startDate) {
      return;
    }

    const standardHours = Math.max(
      Number(selectedProcess.processStandardHours) || 0,
      Number(selectedProcess.labourStandardHours) || 0
    );
    if (!Number.isFinite(standardHours) || standardHours <= 0) {
      return;
    }

    const calculatedDate = getSuggestedPlanEndDate(startDate, standardHours);
    if (!calculatedDate) {
      return;
    }

    form.setValue("eddDate", calculatedDate, {
      shouldDirty: true,
      shouldValidate: true
    });

    lastAutoFilledStartDateRef.current = startDate;
  }, [
    currentActualEndDate,
    form,
    getSuggestedPlanEndDate,
    isEditMode,
    isStartDateDirty,
    selectedProcess?.id,
    selectedProcess?.processStandardHours,
    selectedProcess?.labourStandardHours,
    startDate
  ]);

  const handleAutoFillActualEndDate = (
    processStandardHours?: number,
    labourStandardHours?: number
  ) => {
    const startDateValue = form.getValues("startDate");

    if (!startDateValue) {
      toastManager.showInfo(
        "Start Date Required",
        "Set Start Date first to auto-calculate Actual End Date."
      );
      return;
    }

    const standardHours = Math.max(
      Number(processStandardHours) || 0,
      Number(labourStandardHours) || 0
    );
    if (!Number.isFinite(standardHours) || standardHours <= 0) {
      toastManager.showInfo(
        "Standard Hours Not Set",
        "Standard hours not set - Actual End Date cannot be calculated."
      );
      return;
    }

    const calculatedDate = getSuggestedPlanEndDate(
      startDateValue,
      standardHours
    );
    if (!calculatedDate) {
      toastManager.showInfo(
        "Unable to Calculate",
        "Actual End Date could not be calculated with the current values."
      );
      return;
    }

    form.setValue("eddDate", calculatedDate, {
      shouldDirty: true,
      shouldValidate: true
    });

    lastAutoFilledStartDateRef.current = startDateValue;
  };

  const getWorkflowStatus = (process: any) => {
    return (
      workflowStatusById.get(process?.id) || {
        label: "Not Started",
        textClassName: "aero:!text-neutral-500"
      }
    );
  };

  const handleClearActualEndDate = () => {
    form.setValue("eddDate", "", {
      shouldDirty: true,
      shouldValidate: true
    });

    lastAutoFilledStartDateRef.current = startDate || null;
  };

  const syncSelectedProcess = (process: any) => {
    if (!process) {
      return;
    }

    lastAutoFilledStartDateRef.current = null;

    setSelectedProcess(process);

    const processId = Number(process.id);
    const originalValues = buildFormValuesFromProcess(process);
    const draftValues = processDrafts[processId];

    form.reset(draftValues || originalValues);
  };

  useEffect(() => {
    if (showProcesses || !workflowDetails.length) {
      return;
    }

    if (!selectedProcess?.id) {
      const firstProcess = workflowDetails[0];
      syncSelectedProcess(firstProcess);
      setExpandedProcessId(String(firstProcess.id));
    }
  }, [
    expandedProcessId,
    form,
    selectedProcess?.id,
    showProcesses,
    workflowDetails
  ]);

  useEffect(() => {
    if (!showProcesses || processSelectionTab !== "workchestra") {
      hasFetchedWorkchestraTemplate.current = false;
      return;
    }

    if (hasFetchedWorkchestraTemplate.current) {
      return;
    }

    hasFetchedWorkchestraTemplate.current = true;
    jobOrderWorkflowWorkchestra.refetch();
  }, [processSelectionTab, showProcesses]);

  useEffect(() => {
    if (!showProcesses) {
      hasInitializedProcessSelection.current = false;
      return;
    }

    if (hasInitializedProcessSelection.current || !workflowDetails.length) {
      return;
    }

    const isManualFlowPreviewLoading =
      manualFlowPreview.isLoading || manualFlowPreview.isFetching;

    const previewRows = mapMasterTemplateRows(
      manualFlowPreview?.data?.data?.workflows || []
    );

    if (isManualFlowPreviewLoading && previewRows.length === 0) {
      return;
    }

    const previewLookup = new Map(
      previewRows.map((row) => [row.partProcessId, row])
    );

    const initialSelectedProcesses = workflowDetails
      .filter(
        (process: any) =>
          Number.isFinite(Number(process.partProcessId)) &&
          Number(process.partProcessId) > 0
      )
      .map((process: any) => {
        const matchedPreview = previewLookup.get(Number(process.partProcessId));
        const processMeta = parseProcessName(process.processName);

        return {
          id: Number(process.partProcessId),
          partProcessId: Number(process.partProcessId),
          processId: Number(process.processId ?? process.partProcessId),
          processNo: matchedPreview?.processNo ?? processMeta.processNo,
          processName: matchedPreview?.processName ?? processMeta.processName,
          shopName: matchedPreview?.shopName ?? process.shopName ?? "-",
          processStandardHour:
            matchedPreview?.processStandardHour ??
            process.processStandardHours ??
            process.processStandardHour
        } satisfies ProcessSelectionRow;
      });

    setSelectedProcesses(initialSelectedProcesses);
    setProcessSelectionTab("manual");
    setProcessSearchTerm("");
    setUseWorkchestraAsMainFlow(false);
    hasInitializedProcessSelection.current = true;
  }, [
    showProcesses,
    workflowDetails,
    manualFlowPreview?.data?.data?.workflows,
    manualFlowPreview.isLoading,
    manualFlowPreview.isFetching
  ]);

  useEffect(() => {
    if (!showProcesses || processSelectionTab !== "workchestra") {
      return;
    }

    setSelectedProcesses((prev) => {
      const isSame =
        prev.length === workchestraRows.length &&
        prev.every(
          (item, index) =>
            item.partProcessId === workchestraRows[index]?.partProcessId
        );

      return isSame ? prev : workchestraRows;
    });
  }, [showProcesses, processSelectionTab, workchestraRows]);

  const handleSaveWorkflowSelection = () => {
    if (!selectedProcesses.length) {
      return;
    }

    const partProcesses = selectedProcesses.map((process) => ({
      partProcessId: process.partProcessId,
      processName: `${process.processNo} - ${process.processName}`,
      processStandardHours: process.processStandardHour ?? 0,
      labourStandardHours:
        process.labourStandardHour ?? process.processStandardHour ?? 0
    }));

    addJobOrderWorkflow.mutate(
      {
        jobOrderId: jobOrder.id,
        partProcesses
      },
      {
        onSuccess: () => {
          setShowProcesses(false);
          setSelectedProcess({});
          setProcessDrafts({});
          setExpandedProcessId("");

          // WOR-2034: reset Start Date to process-change timestamp.
          // This ensures manual overrides are replaced when process flow changes.
          const processChangeTimestamp = dayjs().format("YYYY-MM-DDTHH:mm");

          jobOrderWorkflowDetail
            .refetch()
            .then((response: any) => {
              const firstProcess = response?.data?.data?.[0];

              if (!firstProcess?.id) {
                toastManager.showSuccess(
                  "Workflow Updated",
                  "Workflow process steps have been updated."
                );
                return;
              }

              updateWorkflowDate.mutate(
                {
                  id: firstProcess.id,
                  body: {
                    startDate: processChangeTimestamp
                  }
                },
                {
                  onSuccess: () => {
                    jobOrderWorkflowDetail.refetch().then((refresh: any) => {
                      const refreshedFirstProcess = refresh?.data?.data?.find(
                        (item: any) => item.id === firstProcess.id
                      );

                      if (refreshedFirstProcess) {
                        syncSelectedProcess(refreshedFirstProcess);
                        setExpandedProcessId(String(refreshedFirstProcess.id));
                      }

                      toastManager.showSuccess(
                        "Workflow Updated",
                        "Workflow process steps have been updated."
                      );
                    });
                  },
                  onError: (error) => {
                    toastManager.showSuccess(
                      "Workflow Updated",
                      "Workflow process steps have been updated."
                    );

                    toastManager.showInfo(
                      "Start Date Reset Incomplete",
                      error?.message ||
                        "Workflow updated, but Start Date reset failed. Please set Start Date manually."
                    );
                  },
                  onSettled: () => {
                    invalidate([
                      ["MachineSchedule"],
                      ["JobSchedule"],
                      ["JobOrderWorkflow"],
                      ["TechnicianSchedule"],
                      [GetJobWorkload(jobOrder.id).name]
                    ]);
                  }
                }
              );
            })
            .catch(() => {
              toastManager.showSuccess(
                "Workflow Updated",
                "Workflow process steps have been updated."
              );
            });
        },
        onError: (error) => {
          toastManager.showError(
            "Update Process Failed",
            error?.message || "Failed to update workflow process steps."
          );
        }
      }
    );
  };

  const handleCancelWorkflowSelection = () => {
    setShowProcesses(false);
    setProcessSearchTerm("");
    setProcessSelectionTab("manual");
    setUseWorkchestraAsMainFlow(false);
  };

  const handleCancelEdit = () => {
    setProcessDrafts({});

    if (!selectedProcess?.id) {
      form.reset();
      setIsEditMode(false);
      return;
    }

    const selectedProcessFromList = workflowDetails.find(
      (item: any) => item.id === selectedProcess.id
    );

    if (selectedProcessFromList) {
      syncSelectedProcess(selectedProcessFromList);
    }

    setIsEditMode(false);
  };

  const handleSaveChanges = () => {
    const updatesToPersist: Record<number, WorkflowFormValues> = {
      ...processDrafts
    };

    if (selectedProcess?.id && hasUnsavedChanges) {
      updatesToPersist[Number(selectedProcess.id)] = toWorkflowFormValues(
        form.getValues()
      );
    }

    const invalidUpdates = Object.entries(updatesToPersist)
      .map(([workflowIdKey, values]) => {
        const workflowId = Number(workflowIdKey);
        if (!Number.isFinite(workflowId)) {
          return null;
        }

        const errorMessage = getWorkflowValidationErrorMessage(values);
        if (!errorMessage) {
          return null;
        }

        const sourceProcess = workflowDetails.find(
          (item: any) => Number(item.id) === workflowId
        );

        const processName =
          sourceProcess?.processName ||
          `Process ${sourceProcess?.processNo || workflowId}`;

        return {
          workflowId,
          processName,
          errorMessage
        };
      })
      .filter(Boolean);

    if (invalidUpdates.length > 0) {
      const primaryError = invalidUpdates[0] as {
        workflowId: number;
        processName: string;
        errorMessage: string;
      };
      const additionalCount = invalidUpdates.length - 1;
      const suffix =
        additionalCount > 0
          ? ` (+${additionalCount} more invalid process draft${
              additionalCount > 1 ? "s" : ""
            })`
          : "";

      toastManager.showError(
        "Invalid Workflow Draft",
        `${primaryError.processName}: ${primaryError.errorMessage}${suffix}`
      );
      return;
    }

    const workflowUpdates = Object.entries(updatesToPersist)
      .map(([workflowIdKey, values]) => {
        const workflowId = Number(workflowIdKey);
        if (!Number.isFinite(workflowId)) {
          return null;
        }

        const sourceProcess = workflowDetails.find(
          (item: any) => Number(item.id) === workflowId
        );

        if (!sourceProcess) {
          return null;
        }

        const normalizedStart = normalizeDateTimePayload(values.startDate);
        const normalizedEdd = normalizeDateTimePayload(values.eddDate);
        const normalizedTechnicianId = normalizeForeignKeyValue(
          values.technicianId
        );
        const normalizedMachineId = normalizeForeignKeyValue(values.machineId);

        return {
          jobOrderWorkflowId: workflowId,
          startDate: normalizedStart,
          eddDate: normalizedEdd,
          clearEddDate: Boolean(sourceProcess?.eddDate) && !normalizedEdd,
          technicianId: normalizedTechnicianId,
          machineId: normalizedMachineId
        };
      })
      .filter(Boolean);

    if (!workflowUpdates.length) {
      return;
    }

    const selectedProcessId = Number(selectedProcess?.id || 0);

    updateWorkflowDateBatch.mutate(
      {
        jobOrderId: jobOrder.id,
        workflowUpdates
      },
      {
        onSuccess: () => {
          jobOrderWorkflowDetail.refetch().then((response: any) => {
            const refreshedWorkflows = response?.data?.data || [];
            const refreshedSelectedProcess = refreshedWorkflows.find(
              (item: any) => item.id === selectedProcessId
            );

            setProcessDrafts({});

            if (refreshedSelectedProcess) {
              syncSelectedProcess(refreshedSelectedProcess);
            }

            setIsEditMode(false);

            toastManager.showSuccess(
              "✨ Workflow Updated Successfully",
              "Workflow dates have been updated"
            );
          });
        },
        onError: (error) => {
          toastManager.showError(
            "❌ Update Failed",
            getMutationErrorMessage(error, "Failed to update workflow dates.")
          );
        },
        onSettled: () => {
          invalidate([
            ["MachineSchedule"],
            ["JobSchedule"],
            ["JobOrderWorkflow"],
            ["TechnicianSchedule"],
            [GetJobWorkload(jobOrder.id).name]
          ]);
        }
      }
    );
  };

  const isFormLoading =
    updateWorkflowDate.isPending ||
    updateWorkflowDateBatch.isPending ||
    jobOrderWorkflowDetail.isLoading;
  const isSaveDisabled =
    isFormLoading ||
    !hasAnyUnsavedChanges ||
    !isCurrentDraftValid ||
    hasInvalidDrafts;

  return (
    <div className="aero:p-6 aero:space-y-6">
      {!hasProcesses && !showProcesses && (
        <div className="aero:space-y-4">
          <div className="aero:flex aero:flex-wrap aero:items-center aero:justify-between aero:gap-3">
            <div className="aero:flex aero:items-center aero:gap-2">
              <div className="aero:h-8 aero:w-1 aero:rounded-full aero:bg-[#2F5F5A]" />
              <h3 className="aero:!text-md aero:!font-bold aero:!text-[#3C6E68]">
                Workflow Process Steps
              </h3>
            </div>

            {/* WOR-1793 Req #11: Hide button if no canManage */}
            {canManage && (
              <Button
                type="button"
                className="aero:h-10 aero:gap-2 aero:bg-[#245BFF] aero:px-5 aero:text-white aero:hover:bg-[#1D49CC]"
                onClick={() => {
                  setShowProcesses(true);
                }}
              >
                <Plus className="aero:h-4 aero:w-4" />
                Add Process
              </Button>
            )}
          </div>

          <div className="aero:flex aero:min-h-[220px] aero:flex-col aero:items-center aero:justify-center aero:gap-1 aero:rounded-md aero:border aero:border-neutral-300 aero:bg-white aero:px-6 aero:py-10">
            <div className="aero:text-center">
              <div className="aero:text-lg aero:font-semibold aero:text-neutral-900 aero:mb-1">
                No Processes / Workflow has been assigned
              </div>
              <div className="aero:text-sm aero:text-neutral-500">
                Add processes to this job order
              </div>
            </div>
          </div>
        </div>
      )}

      {hasProcesses && !showProcesses && (
        <div className="aero:space-y-4">
          <div className="aero:flex aero:flex-wrap aero:items-center aero:justify-between aero:gap-3">
            <div className="aero:flex aero:items-center aero:gap-2">
              <div className="aero:h-8 aero:w-1 aero:rounded-full aero:bg-[#2F5F5A]" />
              <h3 className="aero:!text-md aero:!font-bold aero:!text-[#3C6E68]">
                Workflow Process Steps
              </h3>
            </div>

            {canManage && !isEditMode && (
              <div className="aero:flex aero:items-center aero:gap-2">
                <Button
                  type="button"
                  className="aero:h-10 aero:gap-2 aero:bg-[#2F5F5A] aero:px-5 aero:text-white aero:hover:bg-[#274F4B]"
                  onClick={() => {
                    setIsEditMode(true);
                  }}
                >
                  <Pencil className="aero:h-4 aero:w-4" />
                  Edit
                </Button>

                <Button
                  type="button"
                  className="aero:h-10 aero:gap-2 aero:bg-[#245BFF] aero:px-5 aero:text-white aero:hover:bg-[#1D49CC]"
                  onClick={() => {
                    setIsEditMode(false);
                    setShowProcesses(true);
                  }}
                >
                  <RotateCw className="aero:h-4 aero:w-4" />
                  Update Process
                </Button>
              </div>
            )}
          </div>

          <div className="aero:rounded-lg aero:border aero:border-[#BFDBFE] aero:bg-[#EFF6FF] aero:px-4 aero:py-3">
            <div className="aero:flex aero:flex-wrap aero:items-center aero:justify-between aero:gap-2">
              <div className="aero:flex aero:items-center aero:gap-2">
                <CalendarDays className="aero:h-4 aero:w-4 aero:text-[#1D4ED8]" />
                <p className="aero:!text-sm aero:!font-semibold aero:!text-[#1E3A8A]">
                  Job EDD (Overall)
                </p>
              </div>

              {jobEddStatus && (
                <span
                  className={cn(
                    "aero:rounded-full aero:border aero:px-2.5 aero:py-1 aero:text-xs aero:font-semibold",
                    jobEddStatus.className
                  )}
                >
                  {jobEddStatus.label}
                </span>
              )}
            </div>

            <p className="aero:mt-1 aero:!text-sm aero:!font-bold aero:!text-[#0F172A]">
              {jobEddDisplay}
            </p>
            <p className="aero:mt-1 aero:!text-xs aero:!text-[#334155]">
              Use this as the main job deadline reference when updating each
              process.
            </p>
          </div>

          <Form {...form}>
            <form
              className="aero:space-y-4"
              onSubmit={(event) => {
                event.preventDefault();

                if (!isSaveDisabled) {
                  handleSaveChanges();
                }
              }}
            >
              <Accordion
                type="single"
                collapsible
                value={expandedProcessId}
                onValueChange={(value) => {
                  if (isEditMode && selectedProcess?.id) {
                    upsertProcessDraft(
                      selectedProcess,
                      toWorkflowFormValues(form.getValues())
                    );
                  }

                  setExpandedProcessId(value);

                  if (!value) {
                    return;
                  }

                  const process = workflowDetails.find(
                    (item: any) => String(item.id) === value
                  );

                  if (process) {
                    syncSelectedProcess(process);
                  }
                }}
                className="aero:space-y-3"
              >
                {workflowDetails.map((process: any, index: number) => {
                  const processId = String(process.id);
                  const isExpanded = expandedProcessId === processId;
                  const isOverdue = Boolean(process.isOverdue);
                  const statusMeta = getWorkflowStatus(process);
                  const effectiveActualEndDate = isExpanded
                    ? currentActualEndDate
                    : process.eddDate;
                  const isOverdueByJobEdd = isActualEndDateOverdueByJobEdd(
                    effectiveActualEndDate,
                    jobEddDate
                  );
                  const shouldShowOverdueWarning = isOverdueByJobEdd;
                  const maxStandardHours = Math.max(
                    Number(process.processStandardHours) || 0,
                    Number(process.labourStandardHours) || 0
                  );
                  const hasMissingStandardHours =
                    !Number.isFinite(maxStandardHours) || maxStandardHours <= 0;
                  const planEndDate = getSuggestedPlanEndDate(
                    isExpanded ? startDate : process.startDate,
                    maxStandardHours
                  );
                  const processTitle = process.shopName
                    ? `${process.processName} (${process.shopName})`
                    : process.processName;

                  return (
                    <AccordionItem
                      key={process.id}
                      value={processId}
                      className={cn(
                        "aero:overflow-hidden aero:rounded-xl aero:border aero:border-neutral-300 aero:px-0",
                        isExpanded ? "aero:bg-white" : "aero:bg-neutral-50",
                        isOverdue && "aero:border-red-300"
                      )}
                    >
                      <AccordionTrigger className="aero:items-center aero:px-4 aero:py-2.5 aero:hover:no-underline aero:[&>svg]:rotate-180 aero:[&[data-state=open]>svg]:rotate-0">
                        <div className="aero:flex aero:items-start aero:gap-3 aero:text-left">
                          <div
                            className={cn(
                              "aero:flex aero:h-6 aero:w-6 aero:shrink-0 aero:items-center aero:justify-center aero:rounded-full aero:!text-xs aero:!font-bold aero:!text-white",
                              isExpanded
                                ? "aero:bg-[#2F5F5A]"
                                : "aero:bg-neutral-400"
                            )}
                          >
                            {index + 1}
                          </div>

                          <div className="aero:space-y-1">
                            <p className="aero:!text-sm aero:!font-bold aero:!text-gray-900">
                              {processTitle}
                            </p>

                            <div className="aero:flex aero:flex-wrap aero:items-center aero:gap-1.5 aero:!text-xs">
                              <CheckCircle2
                                className={cn(
                                  "aero:h-3.5 aero:w-3.5",
                                  statusMeta.textClassName
                                )}
                              />
                              <span
                                className={cn(
                                  "aero:!text-xs aero:!font-medium",
                                  statusMeta.textClassName
                                )}
                              >
                                {statusMeta.label}
                              </span>
                              <span className="aero:!text-neutral-400">•</span>
                              <span className="aero:!text-xs aero:!font-medium aero:!text-[#245BFF]">
                                Process:{" "}
                                {formatStandardHour(
                                  process.processStandardHours
                                )}
                              </span>
                              <span className="aero:!text-neutral-400">•</span>
                              <span className="aero:!text-xs aero:!font-medium aero:!text-[#245BFF]">
                                Labour:{" "}
                                {formatStandardHour(
                                  process.labourStandardHours
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      </AccordionTrigger>

                      <AccordionContent className="aero:border-t aero:border-neutral-200 aero:px-4 aero:pt-3 aero:pb-3">
                        {canManage && isEditMode ? (
                          <div className="aero:grid aero:grid-cols-1 aero:gap-3 aero:md:grid-cols-2">
                            <div className="aero:space-y-2">
                              <div className="aero:flex aero:h-5 aero:items-center">
                                <p className="aero:!text-xs aero:!font-semibold aero:!text-neutral-900">
                                  Start Date
                                </p>
                              </div>
                              <FormDatePickerV2
                                name="startDate"
                                control={form.control}
                                disabled={isFormLoading}
                                placeholder="dd/mm/yyyy"
                                showTime
                                datePickerClassName="aero:h-9"
                              />
                            </div>

                            <div className="aero:space-y-2">
                              <div className="aero:flex aero:h-5 aero:items-center aero:gap-1.5">
                                <p className="aero:!text-xs aero:!font-semibold aero:!text-neutral-900">
                                  Actual End Date
                                </p>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      type="button"
                                      className="aero:inline-flex aero:h-5 aero:w-5 aero:items-center aero:justify-center aero:text-[#245BFF] hover:aero:text-[#1D4ED8] disabled:aero:cursor-not-allowed disabled:aero:opacity-50"
                                      onClick={() =>
                                        handleAutoFillActualEndDate(
                                          process.processStandardHours,
                                          process.labourStandardHours
                                        )
                                      }
                                      disabled={isFormLoading}
                                    >
                                      <Info className="aero:h-3.5 aero:w-3.5" />
                                      <span className="aero:sr-only">
                                        Auto-calculate Actual End Date
                                      </span>
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent
                                    side="top"
                                    className="aero:max-w-[260px]"
                                  >
                                    Click to auto-calculate Actual End Date from
                                    Start Date + Standard Hours.
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                              <FormDatePickerV2
                                name="eddDate"
                                control={form.control}
                                disabled={isFormLoading}
                                placeholder="dd/mm/yyyy"
                                showTime
                                datePickerClassName="aero:h-9"
                              />
                              {currentActualEndDate && (
                                <div className="aero:flex aero:justify-end">
                                  <Button
                                    type="button"
                                    intent="neutral"
                                    design="outline"
                                    className="aero:h-8 aero:px-3 aero:text-xs"
                                    disabled={isFormLoading}
                                    onClick={handleClearActualEndDate}
                                  >
                                    Clear Actual End Date
                                  </Button>
                                </div>
                              )}
                              {shouldShowOverdueWarning && (
                                <p className="aero:text-xs aero:text-amber-600">
                                  This process is overdue. You can still update
                                  the Actual End Date. Checked against Job EDD
                                  (Overall).
                                </p>
                              )}
                            </div>

                            <div className="aero:space-y-2">
                              <div className="aero:flex aero:h-5 aero:items-center aero:gap-1.5">
                                <p className="aero:!text-xs aero:!font-semibold aero:!text-neutral-900">
                                  Machine
                                </p>
                                {hasMachineConflict && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="aero:inline-flex aero:cursor-default aero:items-center aero:text-amber-500">
                                        <AlertTriangle className="aero:h-3.5 aero:w-3.5" />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent
                                      side="top"
                                      className="aero:max-w-[280px]"
                                    >
                                      {renderConflictTooltipContent({
                                        resourceLabel: "Machine",
                                        availabilityIssues:
                                          machineAvailabilityIssues,
                                        conflicts: machineConflicts
                                      })}
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                              </div>
                              <FormSelect
                                name="machineId"
                                placeholder="Select machine"
                                control={form.control}
                                options={machineOptions}
                                triggerClassName="aero:h-9"
                                disabled={
                                  !canManage ||
                                  isFormLoading ||
                                  machineListQuery.isLoading
                                }
                                onValueChange={() =>
                                  form.setValue("technicianId", "")
                                }
                              />
                              {!machineListQuery.isLoading &&
                                selectedProcess?.shopId &&
                                machineOptions.length === 0 && (
                                  <p className="aero:text-xs aero:text-neutral-500 aero:italic">
                                    No machines found for shop{" "}
                                    <span className="aero:font-medium aero:not-italic aero:text-neutral-700">
                                      {selectedProcess?.shopName ??
                                        `#${selectedProcess?.shopId}`}
                                    </span>
                                    . Machine assignment is optional.
                                  </p>
                                )}
                            </div>

                            <div className="aero:space-y-2">
                              <div className="aero:flex aero:h-5 aero:items-center aero:gap-1.5">
                                <p className="aero:!text-xs aero:!font-semibold aero:!text-neutral-900">
                                  Technician
                                </p>
                                {hasConflict && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="aero:inline-flex aero:cursor-default aero:items-center aero:text-amber-500">
                                        <AlertTriangle className="aero:h-3.5 aero:w-3.5" />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent
                                      side="top"
                                      className="aero:max-w-[280px]"
                                    >
                                      {renderConflictTooltipContent({
                                        resourceLabel: "Technician",
                                        availabilityIssues:
                                          technicianAvailabilityIssues,
                                        conflicts: technicianConflicts
                                      })}
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                              </div>
                              <FormSelect
                                name="technicianId"
                                placeholder="Select technician"
                                control={form.control}
                                options={technicianOptions}
                                triggerClassName="aero:h-9"
                                disabled={
                                  !canManage ||
                                  isFormLoading ||
                                  technicianList.isLoading ||
                                  technicianOptions.length === 0
                                }
                              />
                            </div>

                            <div className="aero:space-y-2 aero:md:col-span-2">
                              <p className="aero:!text-xs aero:!font-semibold aero:!text-neutral-900">
                                Plan End Date
                              </p>
                              <div className="aero:relative">
                                <Input
                                  value={
                                    planEndDate
                                      ? formatDisplayDateTime(planEndDate)
                                      : ""
                                  }
                                  placeholder="Auto-calculated"
                                  className="aero:h-9 aero:pr-10 placeholder:aero:italic placeholder:aero:text-neutral-500"
                                  disabled
                                />
                                <CalendarDays className="aero:pointer-events-none aero:absolute aero:right-3 aero:top-1/2 aero:h-3.5 aero:w-3.5 aero:-translate-y-1/2 aero:text-neutral-500" />
                              </div>
                              {hasMissingStandardHours && (
                                <p className="aero:text-xs aero:text-amber-600">
                                  Standard hours not set - Planned End Date
                                  cannot be calculated
                                </p>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="aero:grid aero:grid-cols-1 aero:gap-x-6 aero:gap-y-4 aero:md:grid-cols-2">
                            <div className="aero:space-y-1">
                              <p className="aero:!text-sm aero:!font-semibold aero:!text-neutral-900">
                                Start Date
                              </p>
                              <p className="aero:!text-sm aero:!font-medium aero:!text-neutral-800">
                                {formatDateOrPlaceholder(process.startDate)}
                              </p>
                            </div>

                            <div className="aero:space-y-1">
                              <p className="aero:!text-sm aero:!font-semibold aero:!text-neutral-900">
                                Actual End Date
                              </p>
                              <p className="aero:!text-sm aero:!font-medium aero:!text-neutral-800">
                                {formatDateOrPlaceholder(process.eddDate)}
                              </p>
                            </div>

                            <div className="aero:space-y-1">
                              <p className="aero:!text-sm aero:!font-semibold aero:!text-neutral-900">
                                Machine
                              </p>
                              <div className="aero:flex aero:items-center aero:gap-1.5">
                                <p
                                  className={cn(
                                    "aero:!text-sm",
                                    process.machineName
                                      ? "aero:!font-medium aero:!text-neutral-800"
                                      : "aero:italic aero:!font-normal aero:!text-neutral-500"
                                  )}
                                >
                                  {process.machineName ?? "No machine assigned"}
                                </p>
                                {isExpanded && hasMachineConflict && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="aero:inline-flex aero:cursor-default aero:items-center aero:text-amber-500">
                                        <AlertTriangle className="aero:h-3.5 aero:w-3.5" />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent
                                      side="top"
                                      className="aero:max-w-[280px]"
                                    >
                                      {renderConflictTooltipContent({
                                        resourceLabel: "Machine",
                                        availabilityIssues:
                                          machineAvailabilityIssues,
                                        conflicts: machineConflicts
                                      })}
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                              </div>
                            </div>

                            <div className="aero:space-y-1">
                              <p className="aero:!text-sm aero:!font-semibold aero:!text-neutral-900">
                                Technician
                              </p>
                              <div className="aero:flex aero:items-center aero:gap-1.5">
                                <p
                                  className={cn(
                                    "aero:!text-sm",
                                    process.technicianName
                                      ? "aero:!font-medium aero:!text-neutral-800"
                                      : "aero:italic aero:!font-normal aero:!text-neutral-500"
                                  )}
                                >
                                  {process.technicianName || "Not selected yet"}
                                </p>
                                {isExpanded && hasConflict && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="aero:inline-flex aero:cursor-default aero:items-center aero:text-amber-500">
                                        <AlertTriangle className="aero:h-3.5 aero:w-3.5" />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent
                                      side="top"
                                      className="aero:max-w-[280px]"
                                    >
                                      {renderConflictTooltipContent({
                                        resourceLabel: "Technician",
                                        availabilityIssues:
                                          technicianAvailabilityIssues,
                                        conflicts: technicianConflicts
                                      })}
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                              </div>
                            </div>

                            <div className="aero:space-y-1 aero:md:col-span-2">
                              <p className="aero:!text-sm aero:!font-semibold aero:!text-neutral-900">
                                Plan End Date
                              </p>
                              <p
                                className={cn(
                                  "aero:!text-sm",
                                  planEndDate
                                    ? "aero:!font-medium aero:!text-neutral-800"
                                    : "aero:italic aero:!font-normal aero:!text-neutral-500"
                                )}
                              >
                                {planEndDate
                                  ? formatDisplayDateTime(planEndDate)
                                  : "Auto-calculated"}
                              </p>
                              {hasMissingStandardHours && (
                                <p className="aero:text-xs aero:text-amber-600">
                                  Standard hours not set - Planned End Date
                                  cannot be calculated
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>

              {canManage && isEditMode && (
                <div className="aero:mt-4 aero:flex aero:items-center aero:justify-end aero:gap-2">
                  <Button
                    type="button"
                    intent="neutral"
                    design="outline"
                    className="aero:h-10 aero:px-4"
                    disabled={isFormLoading}
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </Button>

                  <Button
                    type="button"
                    intent="neutral"
                    design="outline"
                    className="aero:h-10 aero:px-4"
                    onClick={() => {
                      if (!selectedProcess?.id) {
                        form.reset();
                        return;
                      }

                      const selectedProcessFromList = workflowDetails.find(
                        (item: any) => item.id === selectedProcess.id
                      );

                      if (selectedProcessFromList) {
                        clearProcessDraft(Number(selectedProcess.id));
                        syncSelectedProcess(selectedProcessFromList);
                      }
                    }}
                  >
                    Reset
                  </Button>

                  <Button
                    type="button"
                    intent="primary"
                    className="aero:h-10 aero:px-6 disabled:aero:!bg-neutral-300 disabled:aero:!text-neutral-500 disabled:aero:hover:!bg-neutral-300"
                    disabled={isSaveDisabled}
                    onClick={handleSaveChanges}
                  >
                    {isFormLoading ? "Saving..." : "Save"}
                  </Button>
                </div>
              )}
            </form>
          </Form>
        </div>
      )}

      {showProcesses && (
        <div className="aero:flex aero:flex-col aero:gap-5">
          <div className="aero:flex aero:items-center aero:gap-2">
            <div className="aero:h-7 aero:w-[2px] aero:rounded-full aero:bg-brand-primary-600" />
            <h3 className="aero:text-lg aero:font-bold aero:leading-7 aero:text-brand-primary-600">
              Workflow Process Steps
            </h3>
          </div>

          <div className="aero:flex aero:flex-col aero:gap-2">
            <Label className="aero:!text-sm aero:!font-bold">Search By</Label>
            <div className="aero:relative">
              <Search className="aero:absolute aero:left-3 aero:top-1/2 aero:h-4 aero:w-4 aero:-translate-y-1/2 aero:text-neutral-400" />
              <Input
                placeholder="Search by process name or shop..."
                value={processSearchTerm}
                onChange={(event) => setProcessSearchTerm(event.target.value)}
                className="aero:h-11 aero:pl-9"
              />
            </div>
          </div>

          <SelectedProcessOverview
            selectedProcesses={selectedProcesses}
            mode="editable"
            helperText={
              processSelectionTab === "workchestra"
                ? "Selected steps follow the Workchestra workflow template."
                : "Selected steps follow your current selections, and table step labels are updated automatically."
            }
            onRemoveProcess={
              processSelectionTab === "manual"
                ? (partProcessId) =>
                    setSelectedProcesses((prev) =>
                      prev.filter(
                        (process) => process.partProcessId !== partProcessId
                      )
                    )
                : undefined
            }
          />

          <Tabs
            value={processSelectionTab}
            onValueChange={(value) =>
              setProcessSelectionTab(value as "manual" | "workchestra")
            }
            className="aero:w-full"
          >
            <TabsList className="aero:grid aero:h-11 aero:w-full aero:grid-cols-2 aero:rounded-lg aero:border aero:border-neutral-300 aero:bg-white aero:p-1">
              <TabsTrigger
                value="workchestra"
                className="aero:h-9 aero:rounded-md aero:border aero:border-transparent aero:text-sm aero:font-semibold aero:text-neutral-700 aero:data-[state=active]:!border-brand-primary-700 aero:data-[state=active]:!bg-brand-primary-50 aero:data-[state=active]:!text-brand-primary-700"
              >
                Workchestra
              </TabsTrigger>
              <TabsTrigger
                value="manual"
                className="aero:h-9 aero:rounded-md aero:border aero:border-transparent aero:text-sm aero:font-semibold aero:text-neutral-700 aero:data-[state=active]:!border-brand-primary-700 aero:data-[state=active]:!bg-brand-primary-50 aero:data-[state=active]:!text-brand-primary-700"
              >
                Manual Flow Input
              </TabsTrigger>
            </TabsList>

            <TabsContent value="workchestra" className="aero:mt-4">
              <div className="aero:flex aero:flex-1 aero:flex-col aero:gap-3">
                <div className="aero:overflow-hidden aero:rounded-md aero:border aero:border-neutral-300 aero:bg-white">
                  <div className="aero:h-[340px] aero:min-h-[340px]">
                    <DataTableStatic
                      data={filteredWorkchestraRows}
                      columns={workchestraColumns as any}
                      features={{ enableSelection: false }}
                    />
                  </div>
                </div>

                <div className="aero:flex aero:items-center aero:gap-2">
                  <Checkbox
                    id="use-workchestra-main-flow"
                    checked={useWorkchestraAsMainFlow}
                    onCheckedChange={(checked) =>
                      setUseWorkchestraAsMainFlow(Boolean(checked))
                    }
                  />
                  <Label
                    htmlFor="use-workchestra-main-flow"
                    className="aero:!text-sm aero:!font-medium aero:!text-neutral-800"
                  >
                    Use this Workchestra workflow as the main flow
                  </Label>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="manual" className="aero:mt-4">
              <div className="aero:flex aero:flex-1 aero:flex-col aero:overflow-hidden aero:rounded-md aero:border aero:border-neutral-300 aero:bg-white">
                <div className="aero:h-[340px] aero:min-h-[340px]">
                  <SimpleTable<ProcessSelectionRow>
                    api={getManualFlowPartProcesses}
                    filterParams={{ search: processSearchTerm }}
                    columns={processColumns}
                    defaultPageSize={10}
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <div className="aero:sticky aero:bottom-0 aero:z-10 aero:-mx-6 aero:-mb-11 aero:flex aero:justify-end aero:gap-2 aero:border-t aero:border-neutral-200 aero:bg-white aero:px-6 aero:py-4">
            <Button
              type="button"
              intent="neutral"
              design="outline"
              className="aero:h-10 aero:min-w-[120px]"
              disabled={addJobOrderWorkflow.isPending}
              onClick={handleCancelWorkflowSelection}
            >
              Cancel
            </Button>

            <Button
              type="button"
              intent="primary"
              onClick={handleSaveWorkflowSelection}
              disabled={
                selectedProcesses.length === 0 ||
                addJobOrderWorkflow.isPending ||
                (processSelectionTab === "workchestra" &&
                  !useWorkchestraAsMainFlow)
              }
              className={cn(
                "aero:h-10 aero:min-w-[180px] aero:gap-2",
                selectedProcesses.length === 0
                  ? "aero:bg-neutral-300 aero:text-neutral-600 hover:aero:bg-neutral-300"
                  : "aero:bg-[#2F4F4F] hover:aero:bg-[#1F3F3F] aero:text-white"
              )}
            >
              {addJobOrderWorkflow.isPending ? (
                "Saving..."
              ) : (
                <>
                  <Save className="aero:h-4 aero:w-4" />
                  Save Workflow
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
